import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { BADGE_TONES, type BadgeTone } from '../lib/badge-tones.js';
import { cn } from '../lib/utils.js';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground',
        brand: 'border-transparent bg-brand-muted text-brand',
        // Design-system tokens (tailwind.preset.cjs `badge`); each resolves light or dark by theme.
        amber: 'border-badge-amber-border bg-badge-amber-bg text-badge-amber-fg',
        purple: 'border-badge-purple-border bg-badge-purple-bg text-badge-purple-fg',
        blue: 'border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg',
        red: 'border-badge-red-border bg-badge-red-bg text-badge-red-fg',
        green: 'border-badge-green-border bg-badge-green-bg text-badge-green-fg',
        gray: 'border-badge-gray-border bg-badge-gray-bg text-badge-gray-fg',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Named palette tone (shared `BADGE_TONES`). Takes precedence over `variant`:
   * renders the tone's bg/border/text and, with `dot`, a matching leading dot.
   */
  tone?: BadgeTone;
  /** Render a leading dot in the tone's accent color. Requires `tone`. */
  dot?: boolean;
}

// forwardRef so Badge can anchor Radix `asChild` triggers (Tooltip, Popover, …).
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, tone, dot, style, children, ...props }, ref) => {
    if (tone) {
      const t = BADGE_TONES[tone];
      return (
        <div
          ref={ref}
          className={cn(badgeVariants({ variant: 'outline' }), className)}
          style={{ backgroundColor: t.bg, borderColor: t.border, color: t.fg, ...style }}
          {...props}
        >
          {dot ? (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: t.dot }}
              aria-hidden
            />
          ) : null}
          {children}
        </div>
      );
    }
    return (
      <div ref={ref} className={cn(badgeVariants({ variant }), className)} style={style} {...props}>
        {children}
      </div>
    );
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
